/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import debug from 'debug';

import { DETAILS, DIV, H2, H3, LABEL, SUMMARY } from 'sdi/components/elements';
import tr, { fromRecord } from 'sdi/locale';
import { IMapBaseLayer } from 'sdi/source';

import {
    getBaseLayerCategories,
    getBaseLayersForCategory,
    getBaseLayersWithoutCategory,
    getCurrentBaseLayerOpt,
} from '../../queries/app';
import { setMapBaseLayer } from '../../events/app';
import { icon } from 'sdi/components/button';
import { notEmpty } from 'sdi/util';

const logger = debug('sdi:webservices');

const renderSelectedBaseLayer = (baselayer: IMapBaseLayer) =>
    DIV(
        'base-layer-selected',
        DIV('label-wrapper', LABEL('', fromRecord(baselayer.name))),
    );

const renderSelected = () =>
    DIV(
        'selected-base-layers',
        H3('', tr.view('selectedBaselayersTitle')),
        getCurrentBaseLayerOpt().map(renderSelectedBaseLayer),
    );

const isSelectedBaselayer = (baselayer: IMapBaseLayer) =>
    getCurrentBaseLayerOpt()
        .map(current => current.id === baselayer.id)
        .getOrElse(false);

const renderBaseLayer = (
    add: (baselayerId: string) => void,
    selected: boolean,
    baseLayer: IMapBaseLayer,
) =>
    selected
        ? DIV(
              {
                  className: 'base-layer-item disabled',
              },
              icon('check'),
              LABEL('', fromRecord(baseLayer.name)),
          )
        : DIV(
              {
                  className: 'base-layer-item',
                  onClick: () => add(baseLayer.id),
              },
              icon('check'),
              LABEL('', fromRecord(baseLayer.name)),
          );

const renderCategoriesList = () =>
    getBaseLayerCategories().map(category =>
        notEmpty(getBaseLayersForCategory(category)).map(baselayers =>
            DETAILS(
                'webservice',
                SUMMARY('webservice-name', fromRecord(category.name)),
                DIV(
                    'base-layer-list',
                    ...baselayers.map(bl =>
                        renderBaseLayer(
                            setMapBaseLayer,
                            isSelectedBaselayer(bl),
                            bl,
                        ),
                    ),
                ),
            ),
        ),
    );

const renderWithoutCategories = () =>
    notEmpty(getBaseLayersWithoutCategory()).map(baselayers =>
        DETAILS(
            'webservice',
            SUMMARY('webservice-name', tr.core('others')),
            DIV(
                'base-layer-list',
                ...baselayers.map(bl =>
                    renderBaseLayer(
                        setMapBaseLayer,
                        isSelectedBaselayer(bl),
                        bl,
                    ),
                ),
            ),
        ),
    );

const renderAllBaseLayers = () =>
    DIV(
        'base-layer-list',
        DIV('with-category', ...renderCategoriesList()),
        DIV('without-category', renderWithoutCategories()),
    );

const webservices = () => {
    return DIV(
        'sidebar-main tool wms-picker',
        H2({}, tr.view('wmsSwitch')),
        DIV({}, tr.view('helptext:wmsSwitchTool')),
        DIV(
            'tool-body',
            renderSelected(),
            DIV(
                'available-base-layers',
                H3('', tr.view('availableBaselayersTitle')),
                renderAllBaseLayers(),
            ),
        ),
    );
};

export default webservices;

logger('loaded');
